/**
 * Экран SOS (P0, design-guest §15.4) — экстренная помощь.
 *
 * Большая красная кнопка → подтверждение («Точно вызвать помощь?») →
 * создаёт срочную заявку (приоритет high, категория ресепшен) и показывает
 * статус «Помощь уже в пути». Опциональное короткое описание ситуации.
 *
 * Двух-шаговое подтверждение защищает от случайного нажатия. Полный i18n,
 * мобайл 375 (кнопка во всю ширину).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card } from '@hsc/ui';
import { useMyStays } from '../guest-stay/useGuestStay.js';
import { pickSosCategory, useCreateSos, useSosCatalog } from './useSos.js';

type Phase = 'idle' | 'confirm' | 'sent';

export function SosPage(): JSX.Element {
  const navigate = useNavigate();
  const catalog = useSosCatalog();
  const stays = useMyStays();
  const sos = useCreateSos();
  const [phase, setPhase] = useState<Phase>('idle');
  const [note, setNote] = useState('');

  const category = pickSosCategory(catalog.data?.categories ?? []);
  const activeStay = (stays.data ?? []).find(
    (s) => s.state === 'checked_in' || s.state === 'confirmed',
  );

  const send = (): void => {
    if (!category) return;
    sos.mutate(
      {
        categoryId: category.id,
        ...(note.trim() !== '' ? { description: note.trim() } : {}),
        ...(activeStay ? { stayId: activeStay.id } : {}),
      },
      { onSuccess: () => setPhase('sent') },
    );
  };

  return (
    <section data-testid="sos-page" className="sos-page">
      <h1 className="hsc-page__title">Экстренная помощь</h1>

      {phase === 'sent' ? (
        <Card>
          <div className="sos-sent" data-testid="sos-sent">
            <span className="sos-sent__icon" aria-hidden="true">
              ✓
            </span>
            <h2 className="hsc-card__title">Помощь уже в пути</h2>
            <p>
              Мы получили ваш сигнал и передали его персоналу. С вами свяжутся в ближайшие
              минуты.
            </p>
            <div className="fleet-drawer__actions">
              <Button onClick={() => navigate('/requests')} data-testid="sos-to-requests">
                Мои заявки
              </Button>
              <Button variant="ghost" onClick={() => navigate('/')}>
                На главную
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card>
          <p className="sos-lead">
            Нажмите кнопку, если вам нужна срочная помощь. Сигнал немедленно уйдёт персоналу
            отеля.
          </p>

          <label className="ta-field">
            <span className="ta-field__label">Что случилось? (необязательно)</span>
            <textarea
              className="ta-input"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Коротко опишите ситуацию"
              data-testid="sos-note"
            />
          </label>

          {phase === 'idle' ? (
            <button
              type="button"
              className="sos-btn sos-btn--lg"
              onClick={() => setPhase('confirm')}
              disabled={catalog.isLoading || !category}
              data-testid="sos-trigger"
            >
              <span className="sos-btn__icon" aria-hidden="true">
                ⚠
              </span>
              SOS · Вызвать помощь
            </button>
          ) : (
            <div className="sos-confirm" data-testid="sos-confirm">
              <p className="sos-confirm__q">Точно вызвать помощь?</p>
              <div className="fleet-drawer__actions">
                <button
                  type="button"
                  className="sos-btn"
                  onClick={send}
                  disabled={sos.isPending}
                  data-testid="sos-confirm-yes"
                >
                  {sos.isPending ? 'Отправляем…' : 'Да, вызвать помощь'}
                </button>
                <Button variant="ghost" onClick={() => setPhase('idle')} data-testid="sos-confirm-no">
                  Отмена
                </Button>
              </div>
            </div>
          )}

          {!category && !catalog.isLoading && (
            <p role="alert" className="ta-muted" style={{ marginTop: 10 }}>
              Не удалось определить службу приёма. Позвоните на ресепшен — контакты в разделе
              «Контакты отеля».
            </p>
          )}
          {sos.isError && (
            <p role="alert" className="fleet-card__err">
              Не удалось отправить сигнал. Попробуйте ещё раз или позвоните на ресепшен.
            </p>
          )}
        </Card>
      )}
    </section>
  );
}
